import clsx from "clsx";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { type CourseId, isChildrenShortCourse, Status } from "~/common/courses";
import type { TPatient } from "~/types/patient";

/**
 * The status → severity colour mapping, extracted so the redesign badge can reuse the SAME semantics
 * (DRY the idea) instead of duplicating the switch and drifting. Legacy behaviour is unchanged.
 */
export function statusBadgeColor(status: TPatient["status"]): StatusBadgeColor {
  // The `default` branch is DELIBERATE, not an oversight: the `Status` enum under-captures the runtime
  // status strings the backend sends (e.g. S_3D_SCANNING is absent), so anything unlisted must fall
  // through to the in-progress colour. Enumerating every member would both duplicate that intent and
  // still fail to cover the values the enum doesn't model.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (status) {
    case Status.TREATMENT_FINISHED:
    case Status.CANCEL_TREATMENT:
      return "grey";
    case Status.WAITING_FOR_A_PAYMENT:
    case Status.UNFILLED_CASE:
    case Status._3D_PLAN_APPROVAL:
    case Status.POWER_CAP_3D_PLAN_APPROVAL:
    case Status.POWER_CAP_RETAKE_IMPRESSIONS:
    case Status.POWER_CAP_WAITING_FOR_A_PAYMENT:
    case Status.RETAKE_IMPRESSIONS:
    case Status.RETAINERS_RETAKE_IMPRESSIONS:
    case Status.RETAINERS_WAITING_FOR_A_PAYMENT:
      return "red";
    default:
      return "green";
  }
}

export function PatientStatusBadge({
  status,
  index = 0,
  course_id,
}: {
  status: TPatient["status"];
  index?: number;
  course_id?: CourseId | null;
}) {
  const intl = useIntl();
  const color = statusBadgeColor(status);

  return (
    <StatusBadge color={color}>
      {index > 0 && status == Status.UNFILLED_CASE
        ? `${intl.formatMessage({
            id: isChildrenShortCourse(course_id)
              ? "pat.block.right.additional_course"
              : "correction",
          })} ${index} - `
        : null}
      <FormattedMessage id={status} />
    </StatusBadge>
  );
}

type StatusBadgeColor = "red" | "green" | "grey";

export function StatusBadge({
  color,
  children,
}: React.PropsWithChildren<{ color: StatusBadgeColor }>) {
  const classNames = {
    red: "bg-red",
    green: "bg-green",
    grey: "bg-grey tw-bg-gray-400",
  };

  return (
    <span className={clsx("label label-sm tw-rounded-[3px] tw-font-normal", classNames[color])}>
      {children}
    </span>
  );
}
